import { fetchRecentMemory, type MemoryEntry } from "./api";

const PAGE_SIZE = 100;
const INCREMENTAL_LIMIT = 100;
const DB_NAME = "sfox-memory-cache";
const STORE = "tiers";
const DB_VERSION = 1;

export const TAB_KEY = "memory_tab";

// ── IndexedDB helpers ────────────────────────────────────────────────────────

let _db: IDBDatabase | null = null;

function openDB(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => { _db = req.result; resolve(req.result); };
    req.onerror = () => reject(req.error);
  });
}

async function dbGet<T>(key: string): Promise<T | null> {
  try {
    const db = await openDB();
    return await new Promise<T | null>((resolve, reject) => {
      const req = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function dbSet(key: string, value: unknown): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // IDB unavailable — proceed without caching
  }
}

async function dbDelete(key: string): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore
  }
}

// ── Cache shape ───────────────────────────────────────────────────────────────

interface MemoryCache {
  entries: MemoryEntry[];
  newestAt: string;
  cachedAt: string;
}

function cacheKey(tier: "T1" | "T2"): string {
  return `memory_cache_${tier}`;
}

function merge(existing: MemoryEntry[], incoming: MemoryEntry[]): MemoryEntry[] {
  const byId = new Map(existing.map((e) => [e.id, e]));
  for (const e of incoming) byId.set(e.id, e);
  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Returns cached entries immediately from IDB — no network call. */
export async function getCached(tier: "T1" | "T2"): Promise<MemoryEntry[] | null> {
  const cache = await dbGet<MemoryCache>(cacheKey(tier));
  return cache?.entries ?? null;
}

/**
 * Syncs the cache against the API and returns the updated full set.
 * - No cache: pages through all entries (slow, first-time only).
 * - Cache exists: fetches only entries newer than newestAt (fast, 1 request).
 */
export async function syncCache(tier: "T1" | "T2"): Promise<MemoryEntry[]> {
  const cache = await dbGet<MemoryCache>(cacheKey(tier));

  if (!cache) {
    const all: MemoryEntry[] = [];
    let before: string | undefined;
    while (true) {
      const page = await fetchRecentMemory(365, tier, PAGE_SIZE, before);
      all.push(...page);
      if (page.length < PAGE_SIZE) break;
      before = page[page.length - 1].created_at;
    }
    if (all.length > 0) {
      await dbSet(cacheKey(tier), {
        entries: all,
        newestAt: all[0].created_at,
        cachedAt: new Date().toISOString(),
      } satisfies MemoryCache);
    }
    return all;
  }

  const daysSince = Math.ceil(
    (Date.now() - new Date(cache.newestAt).getTime()) / 86_400_000
  );
  const days = Math.max(1, daysSince + 1);
  const incoming = await fetchRecentMemory(days, tier, INCREMENTAL_LIMIT);
  const merged = merge(cache.entries, incoming);

  await dbSet(cacheKey(tier), {
    entries: merged,
    newestAt: merged[0]?.created_at ?? cache.newestAt,
    cachedAt: new Date().toISOString(),
  } satisfies MemoryCache);

  return merged;
}

export async function clearCache(tier: "T1" | "T2"): Promise<void> {
  await dbDelete(cacheKey(tier));
}

export async function removeFromCache(tier: "T1" | "T2", id: string): Promise<void> {
  const cache = await dbGet<MemoryCache>(cacheKey(tier));
  if (!cache) return;
  const entries = cache.entries.filter((e) => e.id !== id);
  await dbSet(cacheKey(tier), { ...cache, entries, newestAt: entries[0]?.created_at ?? cache.newestAt });
}

export async function replaceInCache(tier: "T1" | "T2", oldId: string, newEntry: MemoryEntry): Promise<void> {
  const cache = await dbGet<MemoryCache>(cacheKey(tier));
  if (!cache) return;
  const entries = [newEntry, ...cache.entries.filter((e) => e.id !== oldId)];
  await dbSet(cacheKey(tier), { ...cache, entries, newestAt: newEntry.created_at });
}
